import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, Pencil, Trash, Eye, Envelope, Check, X, 
  Globe, CurrencyCircleDollar, Palette, Link as LinkIcon,
  CaretDown, CaretUp, SignOut, Trophy, SoccerBall, UploadSimple, Image, Gear
} from '@phosphor-icons/react';
import TournamentProgressPanel from './TournamentProgressPanel';
import OpenGolfSettingsPanel from './OpenGolfSettingsPanel';
import FeatureAnnouncementCard from '../components/FeatureAnnouncementCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;

// Multi-sport meta — base path + badge per predictor type.
const SPORT_META = {
  wc_2026: { base: 'worldcup2026', label: '⚽ World Cup', badgeBg: 'rgba(99,91,255,0.15)', badgeColor: '#a5a0ff', badgeBorder: 'rgba(99,91,255,0.35)' },
  golf_open_2026: { base: 'opengolf2026', label: '⛳ Open Golf', badgeBg: 'rgba(22,163,74,0.15)', badgeColor: '#4ade80', badgeBorder: 'rgba(22,163,74,0.35)' },
  gaa_final_2026: { base: 'allireland2026', label: '🏆 All-Ireland', badgeBg: 'rgba(217,119,6,0.15)', badgeColor: '#fbbf24', badgeBorder: 'rgba(217,119,6,0.35)' },
};
const sportMeta = (predictorType) => SPORT_META[predictorType || 'wc_2026'] || SPORT_META.wc_2026;

// Reusable image upload component
function ImageUploader({ value, onChange, label, authHeader, testId }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload`, formData, {
        ...authHeader,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const previewUrl = value
    ? (value.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value)
    : null;

  return (
    <div>
      <label className="block text-sm text-gray-400 mb-2">{label}</label>
      <div
        className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById(testId)?.click()}
        data-testid={testId}
      >
        <input
          id={testId}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files[0])}
        />
        {uploading ? (
          <div className="py-2 flex items-center justify-center gap-2 text-gray-400 text-sm">
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#3B82F6', borderTopColor: 'transparent' }} />
            Uploading...
          </div>
        ) : previewUrl ? (
          <div className="flex items-center gap-3">
            <img src={previewUrl} alt="Preview" className="w-12 h-12 object-contain rounded bg-gray-50" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="flex-1 text-left">
              <p className="text-sm text-gray-600 truncate">{value}</p>
              <p className="text-xs text-gray-400">Click or drag to replace</p>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="text-red-400 hover:text-red-600 p-1"
              title="Remove"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="py-2 flex flex-col items-center gap-1 text-gray-400">
            <UploadSimple size={24} />
            <span className="text-sm">Click or drag image here</span>
            <span className="text-xs">PNG, JPG, SVG, WebP (max 5MB)</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Suppression list manager — GDPR erasure requests and any future opt-outs
// land here. Adding an email here means the central send_email() helper will
// silently drop them from every future send. Idempotent and reversible.
function EmailSuppressionCard({ authHeader }) {
  const [rows, setRows] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('GDPR erasure request');
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/email-suppressions`, authHeader);
      setRows(r.data.suppressions || []);
    } catch (e) {
      setMsg({ error: e?.response?.data?.detail || 'Could not load list' });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const add = async () => {
    setMsg(null);
    if (!newEmail.trim()) return;
    try {
      await axios.post(`${API}/admin/email-suppressions`, { email: newEmail.trim(), reason: newReason.trim() }, authHeader);
      setNewEmail('');
      setMsg({ ok: `Added ${newEmail.trim()}` });
      load();
    } catch (e) {
      setMsg({ error: e?.response?.data?.detail || 'Failed' });
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from the suppression list? They'll start receiving TF emails again.`)) return;
    try {
      await axios.delete(`${API}/admin/email-suppressions/${encodeURIComponent(email)}`, authHeader);
      setMsg({ ok: `Removed ${email}` });
      load();
    } catch (e) {
      setMsg({ error: e?.response?.data?.detail || 'Failed' });
    }
  };

  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4" data-testid="suppression-card">
      <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-2">
        Email suppression list <span className="text-xs font-normal text-gray-400">— GDPR erasure / opt-outs</span>
      </h2>
      <p className="text-xs text-gray-500 mb-3">
        Any email on this list is silently dropped from every TF-sent email (entry confirmations, future leaderboard updates, etc.). Add an entry here when someone asks to be deleted.
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="email"
          placeholder="email@example.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded px-2 py-1.5"
          data-testid="suppression-email-input"
        />
        <input
          type="text"
          placeholder="Reason (audit trail)"
          value={newReason}
          onChange={(e) => setNewReason(e.target.value)}
          className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded px-2 py-1.5"
          data-testid="suppression-reason-input"
        />
        <button
          onClick={add}
          className="text-sm bg-[#1D3557] text-white font-bold rounded px-4 py-1.5 hover:bg-[#15293f]"
          data-testid="suppression-add-btn"
        >
          Add
        </button>
      </div>
      {msg && (
        <div className={`mb-3 text-xs ${msg.error ? 'text-red-600' : 'text-green-700'}`}>
          {msg.error || msg.ok}
        </div>
      )}
      {rows.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">No suppressed emails yet.</p>
      ) : (
        <table className="w-full text-sm" data-testid="suppression-table">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">Email</th>
              <th className="py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">Reason</th>
              <th className="py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">Added</th>
              <th className="py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.email} className="border-b last:border-0 border-gray-50">
                <td className="py-2 font-medium text-gray-800">{r.email}</td>
                <td className="py-2 text-xs text-gray-500">{r.reason}</td>
                <td className="py-2 text-xs text-gray-400">{r.added_at ? new Date(r.added_at).toLocaleDateString() : '—'}</td>
                <td className="py-2 text-right">
                  <button onClick={() => remove(r.email)} className="text-xs text-red-600 hover:underline" data-testid={`suppression-remove-${r.email}`}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Resend confirmation email tile — paste a player's email, find their paid
// entries, and re-fire the confirmation. Used when a player swears they never
// got their entry email (typically: it landed in spam, or a legacy direct-
// charge entry slipped past the original verify-payment path).
function ResendConfirmationCard({ authHeader }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null);

  const search = async (e) => {
    e?.preventDefault?.();
    const q = email.trim().toLowerCase();
    if (!q) return;
    setLoading(true); setError(''); setResults(null); setMsg(null);
    try {
      const r = await axios.get(`${API}/admin/entries/lookup`, { ...authHeader, params: { email: q } });
      setResults(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const resend = async (entry, force) => {
    const label = force ? 'FORCE re-send (bypass duplicate check)' : 'Re-send confirmation email';
    if (!window.confirm(`${label} to ${entry.player_email} for "${entry.club_name}"?`)) return;
    setBusyId(entry.id); setMsg(null);
    try {
      const r = await axios.post(
        `${API}/admin/entries/${entry.id}/resend-confirmation`,
        {},
        { ...authHeader, params: force ? { force: true } : {} },
      );
      setMsg({ id: entry.id, text: r.data.message, ok: r.data.sent !== false });
      setTimeout(() => setMsg(null), 6000);
    } catch (err) {
      setMsg({ id: entry.id, text: err.response?.data?.detail || 'Failed', ok: false });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card p-6 mb-6" data-testid="resend-confirmation-card">
      <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-1">
        Resend entry confirmation
      </h2>
      <p className="text-xs text-gray-500 mb-3">
        Look up a player&apos;s paid entries by email and re-fire their &ldquo;You&apos;re In!&rdquo; confirmation. Use <strong>Force resend</strong> when an email_log row exists but the player insists they never received it.
      </p>
      <form onSubmit={search} className="flex gap-2 mb-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="player@example.com"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
          data-testid="resend-lookup-input"
        />
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="px-4 py-2 bg-[#1D3557] text-white text-sm font-semibold rounded-md hover:bg-[#162a44] disabled:opacity-50"
          data-testid="resend-lookup-search"
        >
          {loading ? 'Searching…' : 'Find entries'}
        </button>
      </form>
      {error && <div className="text-sm text-red-600 mb-2" data-testid="resend-lookup-error">{error}</div>}
      {results && results.count === 0 && (
        <p className="text-sm text-gray-500 italic" data-testid="resend-lookup-empty">No paid entries found for {results.email}.</p>
      )}
      {results && results.count > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="resend-lookup-results">
            <thead>
              <tr className="text-xs uppercase text-gray-500 text-left border-b border-gray-200">
                <th className="py-2 pr-3">Player</th>
                <th className="py-2 pr-3">Club</th>
                <th className="py-2 pr-3">Entered</th>
                <th className="py-2 pr-3">Last sent</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.entries.map((e) => (
                <tr key={e.id} className="border-b border-gray-100 align-top" data-testid={`resend-row-${e.id}`}>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-900">{e.player_name}</div>
                    <div className="text-xs text-gray-500">{e.player_email}</div>
                  </td>
                  <td className="py-2 pr-3 text-gray-700">{e.club_name || e.club_id}</td>
                  <td className="py-2 pr-3 text-xs text-gray-500">{e.created_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
                  <td className="py-2 pr-3 text-xs text-gray-500">
                    {e.email_sent_at ? e.email_sent_at.slice(0, 16).replace('T', ' ') : <span className="text-amber-600">never logged</span>}
                  </td>
                  <td className="py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => resend(e, false)}
                      disabled={busyId === e.id}
                      className="px-2.5 py-1 text-xs font-semibold border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      data-testid={`resend-btn-${e.id}`}
                    >
                      {busyId === e.id ? '…' : 'Resend'}
                    </button>
                    <button
                      onClick={() => resend(e, true)}
                      disabled={busyId === e.id}
                      className="px-2.5 py-1 text-xs font-semibold bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
                      data-testid={`force-resend-btn-${e.id}`}
                    >
                      Force resend
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {msg && (
            <div className={`mt-3 text-sm ${msg.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="resend-result-msg">
              {msg.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// GAA final result tile — super admin enters the actual full-time score +
// first-goal minute. Once saved, every club's public All-Ireland predictor
// switches to "match complete" mode and displays the winning entry.
function GaaResultCard({ authHeader }) {
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ kerry_goals: '', kerry_points: '', mayo_goals: '', mayo_points: '', first_goal_minute: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/gaa/result`);
      if (r.data.published) {
        setResult(r.data);
        setForm({
          kerry_goals: r.data.kerry_goals,
          kerry_points: r.data.kerry_points,
          mayo_goals: r.data.mayo_goals,
          mayo_points: r.data.mayo_points,
          first_goal_minute: r.data.first_goal_minute ?? '',
        });
      } else {
        setResult(false);
      }
    } catch {
      setResult(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMsg(null); setSaving(true);
    try {
      const body = {
        kerry_goals: Number(form.kerry_goals),
        kerry_points: Number(form.kerry_points),
        mayo_goals: Number(form.mayo_goals),
        mayo_points: Number(form.mayo_points),
        first_goal_minute: form.first_goal_minute === '' ? null : Number(form.first_goal_minute),
      };
      const r = await axios.post(`${API}/admin/gaa/result`, body, authHeader);
      setResult(r.data);
      setMsg({ ok: true, text: 'Result published — public pages now show the winner.' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || 'Failed to save' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  const clear = async () => {
    if (!window.confirm('Clear the published GAA final result? Public pages will revert to pre-match mode until you enter it again.')) return;
    setSaving(true); setMsg(null);
    try {
      await axios.delete(`${API}/admin/gaa/result`, authHeader);
      setResult(false);
      setForm({ kerry_goals: '', kerry_points: '', mayo_goals: '', mayo_points: '', first_goal_minute: '' });
      setMsg({ ok: true, text: 'Result cleared.' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || 'Failed to clear' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  const numInput = (key, placeholder, max) => (
    <input
      type="number"
      inputMode="numeric"
      min={0}
      max={max}
      value={form[key]}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      placeholder={placeholder}
      className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-center text-lg font-black tabular-nums"
      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
      data-testid={`gaa-result-${key.replace(/_/g, '-')}`}
    />
  );

  const published = result && result.published !== false;

  return (
    <div className="card p-6 mb-6" data-testid="gaa-result-card">
      <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-1">
        All-Ireland Final — Actual Result
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Enter the full-time score + minute of the first goal once the final has ended. Every GAA club&apos;s public predictor will flip to &ldquo;match complete&rdquo; mode with the winning entry displayed.
      </p>

      {published && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800" data-testid="gaa-result-published-banner">
          <strong>Published:</strong> Kerry {result.kerry_goals}-{String(result.kerry_points).padStart(2, '0')} v Mayo {result.mayo_goals}-{String(result.mayo_points).padStart(2, '0')}
          {result.first_goal_minute != null && <> · 1st goal min {result.first_goal_minute}</>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2">Kerry</p>
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Goals</label>
              {numInput('kerry_goals', '1', 10)}
            </div>
            <span className="text-gray-400 text-2xl font-thin mt-4">-</span>
            <div className="flex flex-col items-center">
              <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Points</label>
              {numInput('kerry_points', '15', 40)}
            </div>
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2">Mayo</p>
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Goals</label>
              {numInput('mayo_goals', '0', 10)}
            </div>
            <span className="text-gray-400 text-2xl font-thin mt-4">-</span>
            <div className="flex flex-col items-center">
              <label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Points</label>
              {numInput('mayo_points', '16', 40)}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4 flex items-baseline gap-3">
        <label className="text-xs font-bold uppercase tracking-widest text-gray-600">First goal minute</label>
        {numInput('first_goal_minute', 'e.g. 16', 70)}
        <span className="text-xs text-gray-400">1–70 · leave blank if no goal was scored</span>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving || form.kerry_goals === '' || form.kerry_points === '' || form.mayo_goals === '' || form.mayo_points === ''}
          className="px-4 py-2 bg-[#1D3557] text-white text-sm font-bold rounded-md hover:bg-[#162a44] disabled:opacity-40"
          data-testid="gaa-result-save"
        >
          {saving ? 'Saving…' : published ? 'Update result' : 'Publish result'}
        </button>
        {published && (
          <button
            onClick={clear}
            disabled={saving}
            className="px-3 py-2 border border-red-300 text-red-600 text-sm font-semibold rounded-md hover:bg-red-50 disabled:opacity-40"
            data-testid="gaa-result-clear"
          >
            Clear
          </button>
        )}
        {msg && <span className={`text-xs ${msg.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="gaa-result-msg">{msg.text}</span>}
      </div>
    </div>
  );
}



// Open Golf 2026 waitlist tile — every signup from the post-kickoff World
// Cup demo banner lands here. Use it to fire a Resend broadcast once the
// Golf predictor demo is ready.
function GolfWaitlistCard({ authHeader }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/golf-waitlist`, authHeader);
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load waitlist');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const downloadCsv = () => {
    if (!data?.rows?.length) return;
    const header = ['Created', 'Name', 'Email', 'Club', 'Source'];
    const rows = data.rows.map(r => [r.created_at || '', r.name || '', r.email || '', r.club_name || '', r.source || '']);
    const csv = [header, ...rows].map(line => line.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'open-golf-waitlist.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card p-6 mb-6" data-testid="golf-waitlist-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">Open Golf 2026 waitlist</h2>
          <p className="text-xs text-gray-500 mt-1">Signups from the post-kickoff World Cup demo banner. Total: <strong>{data?.count ?? '…'}</strong></p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-2.5 py-1 text-xs font-semibold border border-gray-300 rounded hover:bg-gray-50" data-testid="golf-waitlist-refresh">Refresh</button>
          <button onClick={downloadCsv} disabled={!data?.rows?.length} className="px-2.5 py-1 text-xs font-semibold bg-[#1D3557] text-white rounded hover:bg-[#162a44] disabled:opacity-40" data-testid="golf-waitlist-csv">Download CSV</button>
          <button onClick={() => setOpen(!open)} className="px-2.5 py-1 text-xs font-semibold border border-gray-300 rounded hover:bg-gray-50" data-testid="golf-waitlist-toggle">{open ? 'Hide' : 'Show'}</button>
        </div>
      </div>
      {error && <p className="text-sm text-red-600 mt-2" data-testid="golf-waitlist-error">{error}</p>}
      {open && data?.rows && (
        data.rows.length === 0 ? (
          <p className="text-sm text-gray-500 italic mt-3" data-testid="golf-waitlist-empty">No signups yet.</p>
        ) : (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm" data-testid="golf-waitlist-table">
              <thead>
                <tr className="text-xs uppercase text-gray-500 text-left border-b border-gray-200">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Club</th>
                  <th className="py-2 pr-3">Source</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.email} className="border-b border-gray-100">
                    <td className="py-2 pr-3 text-xs text-gray-500">{r.created_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
                    <td className="py-2 pr-3 text-gray-900">{r.name || '—'}</td>
                    <td className="py-2 pr-3 text-gray-700">{r.email}</td>
                    <td className="py-2 pr-3 text-gray-700">{r.club_name || '—'}</td>
                    <td className="py-2 pr-3 text-xs text-gray-500">{r.source || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}





// Live activity tile — read-only snapshot of paid entries + revenue across the
// last hour / today (IST midnight) / last 7 days for every live club. Polls a
// single backend endpoint every 60 seconds. Zero impact on live entry flow.
function LiveActivityCard({ authHeader }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/live-activity`, authHeader);
      setData(r.data);
      setUpdatedAt(new Date());
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load live activity');
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtMoney = (cur, amount) => {
    const sym = { EUR: '€', GBP: '£', USD: '$' }[cur] || cur + ' ';
    return `${sym}${(amount || 0).toFixed(0)}`;
  };
  const ago = updatedAt ? Math.max(1, Math.round((Date.now() - updatedAt.getTime()) / 1000)) : null;

  return (
    <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4" data-testid="live-activity-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">
          Live activity
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400" data-testid="live-activity-updated">
            {ago ? `Updated ${ago}s ago` : 'Loading…'}
          </span>
          <button
            onClick={load}
            className="text-xs text-gray-500 hover:text-gray-900 underline"
            data-testid="live-activity-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 mb-2" data-testid="live-activity-error">{error}</div>
      )}

      {data && data.rows && data.rows.length === 0 && (
        <p className="text-sm text-gray-400 py-4 text-center" data-testid="live-activity-empty">
          No live clubs yet.
        </p>
      )}

      {data && data.rows && data.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="live-activity-table">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-2 pr-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">Club</th>
                <th className="py-2 pr-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">Predictor</th>
                <th className="py-2 pr-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 text-right" colSpan={2}>Last hour</th>
                <th className="py-2 pr-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 text-right" colSpan={2}>Today (IST)</th>
                <th className="py-2 pr-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 text-right" colSpan={2}>Last 7 days</th>
                <th className="py-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 text-right" colSpan={2}>All-time</th>
              </tr>
              <tr className="border-b border-gray-100 text-left">
                <th></th>
                <th></th>
                <th className="py-1 pr-1 text-[10px] uppercase tracking-wider text-gray-400 text-right">Entries</th>
                <th className="py-1 pr-3 text-[10px] uppercase tracking-wider text-gray-400 text-right">Rev</th>
                <th className="py-1 pr-1 text-[10px] uppercase tracking-wider text-gray-400 text-right">Entries</th>
                <th className="py-1 pr-3 text-[10px] uppercase tracking-wider text-gray-400 text-right">Rev</th>
                <th className="py-1 pr-1 text-[10px] uppercase tracking-wider text-gray-400 text-right">Entries</th>
                <th className="py-1 pr-3 text-[10px] uppercase tracking-wider text-gray-400 text-right">Rev</th>
                <th className="py-1 pr-1 text-[10px] uppercase tracking-wider text-gray-400 text-right">Entries</th>
                <th className="py-1 text-[10px] uppercase tracking-wider text-gray-400 text-right">Rev</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(row => (
                <tr key={row.club_id} className="border-b last:border-0 border-gray-50" data-testid={`live-activity-row-${row.club_slug}`}>
                  <td className="py-2 pr-3 font-medium text-gray-800">{row.club_name}</td>
                  <td className="py-2 pr-3 text-xs text-gray-500">{row.predictor}</td>
                  <td className="py-2 pr-1 text-right font-semibold text-gray-900">{row.entries_hour}</td>
                  <td className="py-2 pr-3 text-right text-xs text-gray-500">{fmtMoney(row.currency, row.revenue_hour)}</td>
                  <td className="py-2 pr-1 text-right font-semibold text-gray-900">{row.entries_today}</td>
                  <td className="py-2 pr-3 text-right text-xs text-gray-500">{fmtMoney(row.currency, row.revenue_today)}</td>
                  <td className="py-2 pr-1 text-right font-semibold text-gray-900">{row.entries_week}</td>
                  <td className="py-2 pr-3 text-right text-xs text-gray-500">{fmtMoney(row.currency, row.revenue_week)}</td>
                  <td className="py-2 pr-1 text-right font-bold text-gray-900">{row.entries_all}</td>
                  <td className="py-2 text-right text-xs text-gray-700">{fmtMoney(row.currency, row.revenue_all)}</td>
                </tr>
              ))}
              {/* Per-currency totals so EUR + GBP clubs add up cleanly. */}
              {Object.entries(data.totals_by_currency || {}).map(([cur, t]) => (
                <tr key={`total-${cur}`} className="font-bold border-t-2 border-gray-300" data-testid={`live-activity-total-${cur}`}>
                  <td className="py-2 pr-3 text-gray-700">Total ({cur})</td>
                  <td></td>
                  <td className="py-2 pr-1 text-right text-gray-900">{t.entries_hour}</td>
                  <td className="py-2 pr-3 text-right text-xs text-gray-700">{fmtMoney(cur, t.revenue_hour)}</td>
                  <td className="py-2 pr-1 text-right text-gray-900">{t.entries_today}</td>
                  <td className="py-2 pr-3 text-right text-xs text-gray-700">{fmtMoney(cur, t.revenue_today)}</td>
                  <td className="py-2 pr-1 text-right text-gray-900">{t.entries_week}</td>
                  <td className="py-2 pr-3 text-right text-xs text-gray-700">{fmtMoney(cur, t.revenue_week)}</td>
                  <td className="py-2 pr-1 text-right text-gray-900">{t.entries_all}</td>
                  <td className="py-2 text-right text-xs text-gray-700">{fmtMoney(cur, t.revenue_all)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function SuperAdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [expandedClub, setExpandedClub] = useState(null);
  const [activeTab, setActiveTab] = useState('clubs');
  const [recovering, setRecovering] = useState(false);
  const [recoverMsg, setRecoverMsg] = useState(null);
  const [connectLoading, setConnectLoading] = useState({});
  const [onboardings, setOnboardings] = useState([]);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [sendingMagicClubId, setSendingMagicClubId] = useState(null);
  const [demos, setDemos] = useState(null);
  const [demosLoading, setDemosLoading] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [sportFilter, setSportFilter] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    predictor_type: 'wc_2026',
    logo_url: '',
    primary_color: '#00E676',
    secondary_color: '#121212',
    currency: 'GBP',
    entry_fee: 10,
    prizes: [],
    stripe_api_key: '',
    resend_api_key: '',
    resend_from_email: '',
    admin_email: '',
    admin_password: '',
    custom_domain: '',
    subdomain: '',
    pricing_model: 'setup_fee',
    platform_fee_minor: 0,
  });

  const getAuthHeader = () => ({
    auth: { username: 'admin', password }
  });

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/clubs`, getAuthHeader());
      setClubs(response.data.clubs || []);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        setIsAuthenticated(false);
        try { localStorage.removeItem('tf_super_admin_pw'); } catch { /* ignore */ }
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to fetch clubs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.get(`${API}/admin/clubs`, { auth: { username: 'admin', password } });
      setIsAuthenticated(true);
      setError('');
      // Persist so the impersonated club-admin tab (opened via "Open Club
      // Dashboard") can call super-admin-only endpoints like resend-confirmation
      // without re-prompting for the password. Cleared on 401 / session expiry.
      try { localStorage.setItem('tf_super_admin_pw', password); } catch { /* ignore */ }
      fetchClubs();
    } catch (err) {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingClub) {
        // Update existing club
        const updateData = { ...formData };
        delete updateData.admin_password;
        delete updateData.slug;
        
        await axios.put(`${API}/admin/clubs/${editingClub.id}`, updateData, getAuthHeader());
      } else {
        // Create new club
        await axios.post(`${API}/admin/clubs`, formData, getAuthHeader());
      }
      
      setShowForm(false);
      setEditingClub(null);
      resetForm();
      fetchClubs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save club');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clubId) => {
    if (!window.confirm('Are you sure you want to delete this club?')) return;
    
    try {
      await axios.delete(`${API}/admin/clubs/${clubId}`, getAuthHeader());
      fetchClubs();
    } catch (err) {
      setError('Failed to delete club');
    }
  };

  const handleSendLeaderboard = async (clubId) => {
    if (!window.confirm('Send leaderboard update emails to all paid entries?')) return;
    try {
      const response = await axios.post(`${API}/admin/clubs/${clubId}/send-leaderboard`, {}, getAuthHeader());
      const { sent, skipped, failed } = response.data;
      alert(`Emails processed!\n\nSent: ${sent}\nSkipped (already received): ${skipped}\nFailed: ${failed}`);
    } catch (err) {
      alert('Failed to send emails: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleStripeConnect = async (clubId) => {
    setConnectLoading(prev => ({...prev, [clubId]: true}));
    try {
      const response = await axios.post(`${API}/admin/clubs/${clubId}/stripe-connect`, {}, getAuthHeader());
      window.open(response.data.onboarding_url, '_blank');
    } catch (err) {
      alert('Failed to create Connect account: ' + (err.response?.data?.detail || err.message));
    }
    setConnectLoading(prev => ({...prev, [clubId]: false}));
  };

  const checkConnectStatus = async (clubId) => {
    try {
      const response = await axios.get(`${API}/admin/clubs/${clubId}/stripe-connect/status`, getAuthHeader());
      const s = response.data;
      if (s.status === 'active') {
        alert('Stripe Connect is active! This club can now accept payments.');
        fetchClubs();
      } else if (s.status === 'pending') {
        alert('Onboarding not yet complete. The club owner needs to finish filling in their details on Stripe.');
      } else {
        alert('No Connect account found for this club.');
      }
    } catch (err) {
      alert('Error checking status: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openConnectDashboard = async (clubId) => {
    try {
      const response = await axios.post(`${API}/admin/clubs/${clubId}/stripe-connect/dashboard`, {}, getAuthHeader());
      window.open(response.data.dashboard_url, '_blank');
    } catch (err) {
      alert('Failed to open dashboard: ' + (err.response?.data?.detail || err.message));
    }
  };

  const fetchOnboardings = async () => {
    setOnboardLoading(true);
    try {
      const res = await axios.get(`${API}/admin/onboardings`, getAuthHeader());
      setOnboardings(res.data.onboardings || []);
    } catch (err) {
      console.error('Failed to fetch onboardings', err);
    } finally {
      setOnboardLoading(false);
    }
  };

  const fetchDemos = async () => {
    setDemosLoading(true);
    try {
      const res = await axios.get(`${API}/admin/demos`, getAuthHeader());
      setDemos(res.data);
    } catch (err) {
      console.error('Failed to fetch demos', err);
    } finally {
      setDemosLoading(false);
    }
  };

  const downloadDemosCsv = () => {
    // Browsers don't allow setting Authorization on <a> — use fetch + blob
    fetch(`${API}/admin/demos.csv`, { headers: { Authorization: 'Basic ' + btoa(`admin:${password}`) } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `targetfunder_demos_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(err => alert('CSV download failed: ' + err.message));
  };

  const sendWeeklyDigestNow = async () => {
    setDigestSending(true);
    try {
      const r = await axios.post(`${API}/admin/weekly-digest-now`, {}, getAuthHeader());
      alert(`Weekly digest sent to ${r.data.sent_to}`);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDigestSending(false);
    }
  };

  const handleImport = async (onboardId) => {
    setImportingId(onboardId);
    setImportResult(null);
    try {
      const res = await axios.post(`${API}/admin/onboardings/${onboardId}/import`, {}, getAuthHeader());
      setImportResult(res.data);
      fetchOnboardings();
      fetchClubs();
    } catch (err) {
      alert('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImportingId(null);
    }
  };

  const handleSendMagicLink = async (clubId, clubName) => {
    if (!window.confirm(`Send a fresh dashboard magic-link to ${clubName}'s admin email?`)) return;
    try {
      const r = await axios.post(`${API}/admin/clubs/${clubId}/send-magic-link`, {}, getAuthHeader());
      alert(`Magic link sent to ${r.data.admin_email}`);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };


  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      logo_url: '',
      primary_color: '#00E676',
      secondary_color: '#121212',
      currency: 'GBP',
      entry_fee: 10,
      prizes: [],
      stripe_api_key: '',
      resend_api_key: '',
      resend_from_email: '',
      admin_email: '',
      admin_password: '',
      custom_domain: '',
      subdomain: '',
      pricing_model: 'setup_fee',
      platform_fee_minor: 0,
    });
  };

  const startEdit = (club) => {
    setEditingClub(club);
    setFormData({
      name: club.name,
      slug: club.slug,
      logo_url: club.logo_url || '',
      primary_color: club.primary_color || '#00E676',
      secondary_color: club.secondary_color || '#121212',
      currency: club.currency || 'GBP',
      entry_fee: club.entry_fee || 10,
      prizes: club.prizes || [],
      stripe_api_key: club.stripe_api_key || '',
      resend_api_key: club.resend_api_key || '',
      resend_from_email: club.resend_from_email || '',
      admin_email: club.admin_email || '',
      admin_password: '',
      custom_domain: club.custom_domain || '',
      subdomain: club.subdomain || '',
      pricing_model: club.pricing_model || 'setup_fee',
      platform_fee_minor: club.platform_fee_minor ?? 0,
    });
    setShowForm(true);
  };

  // Prize management helpers
  const addPrize = () => {
    setFormData({
      ...formData,
      prizes: [...formData.prizes, { position: formData.prizes.length + 1, title: '', description: '', sponsor_name: '', sponsor_logo_url: '' }]
    });
  };

  const updatePrize = (index, field, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = { ...newPrizes[index], [field]: value };
    setFormData({ ...formData, prizes: newPrizes });
  };

  const recoverPayments = async () => {
    setRecovering(true);
    setRecoverMsg(null);
    try {
      const res = await axios.post(`${API}/admin/recover-payments`, {}, getAuthHeader());
      setRecoverMsg(res.data);
    } catch (err) {
      setRecoverMsg({ error: err.response?.data?.detail || 'Failed to recover payments' });
    } finally {
      setRecovering(false);
      setTimeout(() => setRecoverMsg(null), 5000);
    }
  };

  const removePrize = (index) => {
    const newPrizes = formData.prizes.filter((_, i) => i !== index);
    // Reindex positions
    newPrizes.forEach((p, i) => p.position = i + 1);
    setFormData({ ...formData, prizes: newPrizes });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--bg-main)' }}>
        <div className="max-w-md w-full">
          <div className="card p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="super-admin-title">
                Super Admin
              </h1>
              <p className="text-gray-400">World Cup 2026 Predictor Management</p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field w-full"
                  placeholder="Enter admin password"
                  required
                  data-testid="super-admin-password"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-900/20 border border-red-500 text-red-400 text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
                data-testid="super-admin-login-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)' }}>
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="dashboard-title">
              Super Admin Dashboard
            </h1>
            <p className="text-xs text-gray-400">Manage World Cup Predictor Clubs</p>
          </div>
          
          <div className="flex items-center gap-3">
            {activeTab === 'clubs' && (
              <>
                <button
                  onClick={recoverPayments}
                  disabled={recovering}
                  className="btn-secondary text-sm py-2 px-3 flex items-center gap-1.5"
                  data-testid="recover-payments-btn"
                  title="Check Stripe for any paid entries stuck as pending"
                >
                  {recovering ? (
                    <><div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#666', borderTopColor: 'transparent' }} /> Checking...</>
                  ) : (
                    <><CurrencyCircleDollar size={16} /> Recover Payments</>
                  )}
                </button>
                <button
                  onClick={() => { setShowForm(true); setEditingClub(null); resetForm(); }}
                  className="btn-primary flex items-center gap-2"
                  data-testid="add-club-btn"
                >
                  <Plus size={18} weight="bold" /> Add Club
                </button>
              </>
            )}
            <button
              onClick={() => { setIsAuthenticated(false); setPassword(''); }}
              className="text-gray-400 hover:text-gray-800 transition-colors"
              data-testid="logout-btn"
            >
              <SignOut size={24} />
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-7xl mx-auto mt-3 flex gap-1">
          <button
            onClick={() => setActiveTab('clubs')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-colors ${activeTab === 'clubs' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-clubs"
          >
            <Globe size={16} /> Clubs
          </button>
          <button
            onClick={() => { setActiveTab('tournament'); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-colors ${activeTab === 'tournament' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-tournament"
          >
            <SoccerBall size={16} /> Tournament Progress
          </button>
          <button
            onClick={() => { setActiveTab('onboardings'); fetchOnboardings(); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-colors ${activeTab === 'onboardings' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-onboardings"
          >
            <Plus size={16} /> Onboardings
          </button>
          <button
            onClick={() => { setActiveTab('demos'); fetchDemos(); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-colors ${activeTab === 'demos' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-demos"
          >
            <Eye size={16} /> Demos
          </button>
          <button
            onClick={() => setActiveTab('golf')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-colors ${activeTab === 'golf' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-golf"
          >
            <SoccerBall size={16} /> Open Golf
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500 text-red-400">
            {error}
          </div>
        )}

        {recoverMsg && (
          <div className={`mb-6 p-4 rounded-lg border ${recoverMsg.error ? 'bg-red-50 border-red-200 text-red-600' : 'bg-green-50 border-green-200 text-green-700'}`} data-testid="recover-msg">
            {recoverMsg.error ? recoverMsg.error : `Recovered ${recoverMsg.recovered} payments (${recoverMsg.total_pending} were pending)`}
          </div>
        )}

        <LiveActivityCard authHeader={getAuthHeader()} />
        <ResendConfirmationCard authHeader={getAuthHeader()} />
        <GaaResultCard authHeader={getAuthHeader()} />
        <FeatureAnnouncementCard authHeader={getAuthHeader()} />
        <GolfWaitlistCard authHeader={getAuthHeader()} />
        <EmailSuppressionCard authHeader={getAuthHeader()} />

        {/* Club Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {editingClub ? 'Edit Club' : 'Add New Club'}
                </h2>
                <button onClick={() => { setShowForm(false); setEditingClub(null); }} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Basic Info */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Club Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                        setFormData({...formData, name, ...(!editingClub ? { slug: autoSlug } : {})});
                      }}
                      className="input-field w-full"
                      placeholder="Manchester United FC"
                      required
                      data-testid="club-name-input"
                    />
                    {!editingClub && formData.name && (
                      <p className="text-xs text-gray-500 mt-1">Club link: <span className="font-mono">/{formData.predictor_type === 'golf_open_2026' ? 'opengolf2026' : formData.predictor_type === 'gaa_final_2026' ? 'allireland2026' : 'worldcup2026'}/{formData.slug}</span></p>
                    )}
                  </div>

                  {!editingClub && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Predictor *</label>
                      <select
                        value={formData.predictor_type}
                        onChange={(e) => setFormData({ ...formData, predictor_type: e.target.value })}
                        className="input-field w-full"
                        data-testid="club-predictor-type"
                      >
                        <option value="wc_2026">⚽ World Cup 2026</option>
                        <option value="golf_open_2026">⛳ Open Golf 2026</option>
                        <option value="gaa_final_2026">🏆 All-Ireland Final 2026</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Same slug can have ONE row per predictor. Switching this lets you create a 2nd product for an existing club.
                      </p>
                    </div>
                  )}

                  <div>
                    <ImageUploader
                      value={formData.logo_url}
                      onChange={(url) => setFormData({...formData, logo_url: url})}
                      label="Club Logo"
                      authHeader={getAuthHeader()}
                      testId="club-logo-upload"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({...formData, currency: e.target.value})}
                      className="input-field w-full"
                    >
                      <option value="GBP">GBP (£)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                      <option value="AUD">AUD ($)</option>
                      <option value="CAD">CAD ($)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Entry Fee</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.entry_fee}
                      onChange={(e) => setFormData({...formData, entry_fee: parseFloat(e.target.value)})}
                      className="input-field w-full"
                    />
                  </div>

                  {/* Pricing model — controls whether application_fee_amount
                      is added on each entry checkout. setup_fee = club paid
                      upfront, keeps 100%. per_entry = TF deducts £1/€1 per
                      entry. */}
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-2">Pricing Model</label>
                    <select
                      value={formData.pricing_model}
                      onChange={(e) => {
                        const model = e.target.value;
                        // Auto-set platform_fee_minor sensible default when switching
                        let fee = formData.platform_fee_minor;
                        if (model === 'per_entry' && !fee) {
                          fee = 99; // £0.99 / €0.99 — TF retains ~57p/50c after Stripe takes ~42p/49c
                        } else if (model === 'setup_fee') {
                          fee = 0;
                        }
                        setFormData({ ...formData, pricing_model: model, platform_fee_minor: fee });
                      }}
                      className="input-field w-full"
                      data-testid="pricing-model-select"
                    >
                      <option value="setup_fee">Setup Fee (one-off £195/€249, club keeps 100%)</option>
                      <option value="per_entry">Per Entry (£0 upfront, app fee per entry)</option>
                    </select>
                  </div>

                  {formData.pricing_model === 'per_entry' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-400 mb-2">
                        Platform Fee per Entry (in pence/cents — e.g. 100 = £1.00 / €1.00)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.platform_fee_minor || 0}
                        onChange={(e) => setFormData({ ...formData, platform_fee_minor: parseInt(e.target.value || '0', 10) })}
                        className="input-field w-full"
                        data-testid="platform-fee-minor-input"
                      />
                    </div>
                  )}

                  {/* Colors */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Primary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                        className="w-12 h-10 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                        className="input-field flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Secondary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({...formData, secondary_color: e.target.value})}
                        className="w-12 h-10 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({...formData, secondary_color: e.target.value})}
                        className="input-field flex-1"
                      />
                    </div>
                  </div>

                  {/* Prizes */}
                  <div className="md:col-span-2 border-t pt-4 mt-2" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
                        <Trophy size={16} /> Prizes
                      </h3>
                      <button
                        type="button"
                        onClick={addPrize}
                        className="text-xs btn-secondary py-1 px-3 flex items-center gap-1"
                      >
                        <Plus size={14} /> Add Prize
                      </button>
                    </div>
                  </div>

                  {formData.prizes.map((prize, index) => (
                    <div key={index} className="md:col-span-2 p-4 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-color)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-bold text-gray-400">Prize #{index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removePrize(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Title (e.g., "1st Place")</label>
                          <input
                            type="text"
                            value={prize.title}
                            onChange={(e) => updatePrize(index, 'title', e.target.value)}
                            className="input-field w-full text-sm"
                            placeholder="1st Place"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Description (e.g., "£100 Cash")</label>
                          <input
                            type="text"
                            value={prize.description}
                            onChange={(e) => updatePrize(index, 'description', e.target.value)}
                            className="input-field w-full text-sm"
                            placeholder="£100 Cash Prize"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Sponsor Name (optional)</label>
                          <input
                            type="text"
                            value={prize.sponsor_name || ''}
                            onChange={(e) => updatePrize(index, 'sponsor_name', e.target.value)}
                            className="input-field w-full text-sm"
                            placeholder="Local Business Name"
                          />
                        </div>
                        <div>
                          <ImageUploader
                            value={prize.sponsor_logo_url || ''}
                            onChange={(url) => updatePrize(index, 'sponsor_logo_url', url)}
                            label="Sponsor Logo (optional)"
                            authHeader={getAuthHeader()}
                            testId={`sponsor-logo-upload-${index}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Stripe */}
                  <div className="md:col-span-2 border-t pt-4 mt-2" style={{ borderColor: 'var(--border-color)' }}>
                    <h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
                      <CurrencyCircleDollar size={16} /> Payment Configuration
                    </h3>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-2">Club's Own Stripe Key (optional)</label>
                    <input
                      type="password"
                      value={formData.stripe_api_key}
                      onChange={(e) => setFormData({...formData, stripe_api_key: e.target.value})}
                      className="input-field w-full font-mono"
                      placeholder="sk_live_..."
                    />
                    <p className="text-xs text-gray-500 mt-1">If the club has their own Stripe account. Otherwise use Stripe Connect after saving.</p>
                  </div>

                  {/* Club Admin */}
                  <div className="md:col-span-2 border-t pt-4 mt-2" style={{ borderColor: 'var(--border-color)' }}>
                    <h3 className="text-sm font-bold text-gray-300 mb-3">Club Admin Credentials</h3>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Admin Email *</label>
                    <input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                      className="input-field w-full"
                      placeholder="admin@theirclub.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Admin Password {editingClub ? '(leave blank to keep)' : '*'}
                    </label>
                    <input
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                      className="input-field w-full"
                      placeholder="••••••••"
                      required={!editingClub}
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-6 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <button type="button" onClick={() => { setShowForm(false); setEditingClub(null); }} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="save-club-btn">
                    {loading ? 'Saving...' : (editingClub ? 'Update Club' : 'Create Club')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Clubs List */}
        {activeTab === 'clubs' && (
          <>
            {/* Sport filter chips */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="sport-filter-chips">
              {[
                { key: 'all', label: 'All sports' },
                { key: 'wc_2026', label: '⚽ World Cup' },
                { key: 'golf_open_2026', label: '⛳ Open Golf' },
                { key: 'gaa_final_2026', label: '🏆 All-Ireland' },
              ].map((chip) => {
                const count = chip.key === 'all' ? clubs.length : clubs.filter((c) => (c.predictor_type || 'wc_2026') === chip.key).length;
                return (
                  <button
                    key={chip.key}
                    onClick={() => setSportFilter(chip.key)}
                    className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${sportFilter === chip.key ? 'bg-white text-gray-900 border-white' : 'text-gray-400 border-gray-600 hover:border-gray-400'}`}
                    data-testid={`sport-filter-${chip.key}`}
                  >
                    {chip.label} <span className="opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>
            {loading && !showForm ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : clubs.length === 0 ? (
          <div className="card p-12 text-center">
            <p className="text-gray-400 mb-4">No clubs yet. Add your first club to get started!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {clubs.filter((c) => sportFilter === 'all' || (c.predictor_type || 'wc_2026') === sportFilter).map((club) => (
              <div key={club.id} className="card overflow-hidden" data-testid={`club-row-${club.slug}`}>
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={() => setExpandedClub(expandedClub === club.id ? null : club.id)}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 flex items-center justify-center font-bold text-lg"
                      style={{ background: club.primary_color, color: '#000' }}
                    >
                      {club.logo_url ? (
                        <img src={assetUrl(club.logo_url)} alt={club.name} className="w-full h-full object-contain" />
                      ) : (
                        club.name.charAt(0)
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{club.name}</h3>
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: sportMeta(club.predictor_type).badgeBg, color: sportMeta(club.predictor_type).badgeColor, border: `1px solid ${sportMeta(club.predictor_type).badgeBorder}` }}
                          data-testid={`sport-badge-${club.slug}`}
                        >
                          {sportMeta(club.predictor_type).label}
                        </span>
                        <p className="text-sm text-gray-400">{club.currency} {club.entry_fee?.toFixed(2)} entry</p>
                        {/* Pricing-model badge — instantly tells super admin
                            whether this club is on per-entry (we take 99p/99c)
                            or paid the one-off setup fee (we take 0). */}
                        {club.pricing_model === 'per_entry' ? (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(230,57,70,0.15)', color: '#E63946', border: '1px solid rgba(230,57,70,0.35)' }}
                            title="Pay per entry — TF takes 99p/99c application fee per ticket"
                            data-testid={`pricing-badge-${club.slug}`}
                          >
                            Per entry
                          </span>
                        ) : (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(29,53,87,0.15)', color: '#9bb3d6', border: '1px solid rgba(29,53,87,0.35)' }}
                            title="One-off setup fee — TF takes 0 per ticket"
                            data-testid={`pricing-badge-${club.slug}`}
                          >
                            Setup fee
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${club.stripe_payments_approved ? 'bg-green-500' : (club.stripe_api_key || club.stripe_connect_onboarding_complete) ? 'bg-amber-500' : 'bg-gray-500'}`} title={club.stripe_payments_approved ? 'Payments approved' : club.stripe_api_key ? 'Own Stripe key' : club.stripe_connect_onboarding_complete ? 'Awaiting approval' : 'No Stripe'} />
                      <span className={`w-2 h-2 rounded-full ${club.is_active ? 'bg-green-500' : 'bg-red-500'}`} title={club.is_active ? 'Active' : 'Inactive'} />
                    </div>
                    {expandedClub === club.id ? <CaretUp size={20} /> : <CaretDown size={20} />}
                  </div>
                </div>
                
                {expandedClub === club.id && (
                  <div className="border-t p-4" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-elevated)' }}>
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Club URL Name</p>
                        <p className="font-mono text-sm">{club.slug}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Admin Email</p>
                        <p className="text-sm">{club.admin_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Entry Fee</p>
                        <p className="text-sm">{club.currency} {club.entry_fee?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Pricing Model</p>
                        <p className="text-sm">
                          {club.pricing_model === 'per_entry'
                            ? <>Pay per entry <span className="text-gray-500">— TF takes {club.currency === 'EUR' ? '99c' : '99p'}/entry</span></>
                            : <>One-off setup fee <span className="text-gray-500">— TF takes 0/entry</span></>}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Created</p>
                        <p className="text-sm">{new Date(club.created_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Payments</p>
                        <p className="text-sm">
                          {club.stripe_api_key ? 'Own Stripe Key' : 
                           club.stripe_connect_onboarding_complete && club.stripe_payments_approved ? 'Stripe Connect (Approved)' :
                           club.stripe_connect_onboarding_complete && !club.stripe_payments_approved ? 'Stripe Connect (Awaiting Approval)' :
                           club.stripe_connect_account_id ? 'Stripe Connect (Pending Setup)' : 'Not configured'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Stripe Connect Controls */}
                    {!club.stripe_api_key && (
                      <div className="flex items-center gap-2 mb-4 flex-wrap">
                        {!club.stripe_connect_onboarding_complete && (
                          <button
                            onClick={() => handleStripeConnect(club.id)}
                            disabled={connectLoading[club.id]}
                            className="btn btn-sm bg-indigo-600 text-white hover:bg-indigo-700 text-xs px-3 py-1.5 rounded"
                            data-testid={`connect-stripe-${club.slug}`}
                          >
                            {connectLoading[club.id] ? 'Creating...' : club.stripe_connect_account_id ? 'Resend Onboarding Link' : 'Set Up Stripe Connect'}
                          </button>
                        )}
                        {club.stripe_connect_account_id && !club.stripe_connect_onboarding_complete && (
                          <button
                            onClick={() => checkConnectStatus(club.id)}
                            className="btn btn-sm bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs px-3 py-1.5 rounded"
                            data-testid={`check-connect-${club.slug}`}
                          >
                            Check Status
                          </button>
                        )}
                        {club.stripe_connect_onboarding_complete && (
                          <button
                            onClick={() => openConnectDashboard(club.id)}
                            className="btn btn-sm bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs px-3 py-1.5 rounded"
                            data-testid={`connect-dashboard-${club.slug}`}
                          >
                            Open Stripe Dashboard
                          </button>
                        )}
                        {club.stripe_connect_onboarding_complete && !club.stripe_payments_approved && (
                          <button
                            onClick={async () => {
                              try {
                                // Preflight check first
                                const pre = await axios.post(`${API}/admin/clubs/${club.id}/launch-preflight`, {}, getAuthHeader());
                                const warnings = pre.data.warnings || [];
                                if (warnings.length) {
                                  const msg = 'Pre-launch check:\n\n' + warnings.map(w => (w.severity === 'error' ? '❌ ' : '⚠️ ') + w.message).join('\n') + (pre.data.can_launch ? '\n\nLaunch anyway?' : '\n\nCannot launch yet.');
                                  if (!pre.data.can_launch) { alert(msg); return; }
                                  if (!window.confirm(msg)) return;
                                } else {
                                  if (!window.confirm(`Approve Stripe payments AND launch ${club.name} now? Both officers will be emailed.`)) return;
                                }
                                const r = await axios.post(`${API}/admin/clubs/${club.id}/approve-and-launch`, {}, getAuthHeader());
                                alert(`✅ Club is live and ${(r.data.notified || []).length} officer(s) notified.\n\nPublic URL: ${r.data.public_url}`);
                                fetchClubs();
                              } catch (err) {
                                alert('Failed: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="btn btn-sm bg-green-600 text-white hover:bg-green-700 text-xs px-3 py-1.5 rounded font-bold"
                            data-testid={`approve-and-launch-${club.slug}`}
                          >
                            Approve & Launch
                          </button>
                        )}
                        {club.is_live && (
                          <button
                            onClick={async () => {
                              try {
                                const r = await axios.post(`${API}/admin/clubs/${club.id}/impersonate`, {}, getAuthHeader());
                                window.open(r.data.login_url, '_blank', 'noopener');
                              } catch (err) {
                                alert('Failed: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="btn btn-sm bg-[#1D3557] text-white hover:bg-[#15293f] text-xs px-3 py-1.5 rounded font-bold"
                            data-testid={`open-club-dashboard-${club.slug}`}
                            title="Opens this club's admin dashboard in a new tab (super-admin view-as)"
                          >
                            Open Club Dashboard
                          </button>
                        )}
                        {club.is_live && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Re-send the launch email to ${club.name}'s officers? Use this when the original "site is LIVE" email got skipped (e.g. the club skipped the welcome page after Stripe).`)) return;
                              try {
                                const r = await axios.post(`${API}/admin/clubs/${club.id}/resend-launch-email`, {}, getAuthHeader());
                                alert(`✅ Launch email re-sent to ${(r.data.notified || []).length} officer(s).`);
                              } catch (err) {
                                alert('Failed: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="btn btn-sm bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs px-3 py-1.5 rounded"
                            data-testid={`resend-launch-email-${club.slug}`}
                          >
                            Re-send launch email
                          </button>
                        )}
                        {club.is_live && (club.predictor_type || 'wc_2026') === 'wc_2026' && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Reset the leaderboard-broadcast cooldown for ${club.name}? This lets you send another update today — use only for testing or genuine corrections.`)) return;
                              try {
                                await axios.post(`${API}/admin/clubs/${club.id}/reset-broadcast-cooldown`, {}, getAuthHeader());
                                alert(`✅ Cooldown cleared for ${club.name}. The club admin can now send another leaderboard update immediately.`);
                              } catch (err) {
                                alert('Failed: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="btn btn-sm bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs px-3 py-1.5 rounded"
                            data-testid={`reset-broadcast-cooldown-${club.slug}`}
                            title="Clears the once-per-day broadcast rate limit for this club so the admin can send again immediately."
                          >
                            Reset broadcast cooldown
                          </button>
                        )}
                        {club.stripe_payments_approved && (
                          <button
                            onClick={async () => {
                              if (!window.confirm('This will stop the club from receiving payments. Are you sure?')) return;
                              try {
                                await axios.post(`${API}/admin/clubs/${club.id}/revoke-payments`, {}, getAuthHeader());
                                alert('Payments revoked');
                                fetchClubs();
                              } catch (err) {
                                alert('Failed: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="btn btn-sm bg-red-100 text-red-600 hover:bg-red-200 text-xs px-3 py-1.5 rounded"
                            data-testid={`revoke-payments-${club.slug}`}
                          >
                            Revoke Payments
                          </button>
                        )}
                      </div>
                    )}
                    
                    {/* Pending Review Alert */}
                    {club.stripe_connect_onboarding_complete && !club.stripe_payments_approved && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 text-xs text-amber-800">
                        <strong>Action required:</strong> This club has connected Stripe but payments are not yet approved. Check their Stripe Connect account details and uploaded bank statement before approving.
                      </div>
                    )}

                    {/* Live Status */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className={`w-2.5 h-2.5 rounded-full ${club.is_live ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <span className="text-sm font-medium">{club.is_live ? 'Public page is LIVE' : 'Public page is NOT LIVE (Coming Soon)'}</span>
                      {club.is_live && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('This will show a "Coming Soon" page to visitors. Are you sure?')) return;
                            try {
                              await axios.post(`${API}/admin/clubs/${club.id}/take-offline`, {}, getAuthHeader());
                              alert('Club taken offline');
                              fetchClubs();
                            } catch (err) {
                              alert('Failed: ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                          className="btn btn-sm bg-gray-200 text-gray-600 hover:bg-gray-300 text-xs px-3 py-1 rounded ml-2"
                          data-testid={`take-offline-${club.slug}`}
                        >
                          Take Offline
                        </button>
                      )}
                      {!club.is_live && club.stripe_payments_approved && (
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/clubs/${club.id}/make-live`, {}, getAuthHeader());
                              alert('Club is now live!');
                              fetchClubs();
                            } catch (err) {
                              alert('Failed: ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                          className="btn btn-sm bg-green-600 text-white hover:bg-green-700 text-xs px-3 py-1 rounded font-bold ml-2"
                          data-testid={`relaunch-${club.slug}`}
                        >
                          Re-launch
                        </button>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                      <a 
                        href={`/${sportMeta(club.predictor_type).base}/${club.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
                      >
                        <Eye size={16} /> View Page
                      </a>
                      <a 
                        href={`/${sportMeta(club.predictor_type).base}/${club.slug}/admin?sa=1`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
                      >
                        <Eye size={16} /> Club Dashboard
                      </a>
                      <button
                        onClick={() => startEdit(club)}
                        className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
                      >
                        <Pencil size={16} /> Edit
                      </button>
                      {(club.predictor_type || 'wc_2026') === 'wc_2026' && (
                        <button
                          onClick={() => handleSendLeaderboard(club.id)}
                          className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
                          disabled={loading}
                          title="Send leaderboard to all entries"
                        >
                          <Envelope size={16} /> Send Leaderboard
                        </button>
                      )}
                      <button
                        onClick={() => {
                          const link = `${window.location.origin}/${sportMeta(club.predictor_type).base}/${club.slug}`;
                          navigator.clipboard.writeText(link);
                          alert('Link copied!');
                        }}
                        className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
                      >
                        <LinkIcon size={16} /> Copy Link
                      </button>
                      <button
                        onClick={() => handleDelete(club.id)}
                        className="btn-secondary text-sm py-2 px-3 flex items-center gap-2 text-red-400 border-red-400/50 hover:border-red-400"
                      >
                        <Trash size={16} /> Delete
                      </button>
                      <button
                        onClick={async () => {
                          setSendingMagicClubId(club.id);
                          await handleSendMagicLink(club.id, club.name);
                          setSendingMagicClubId(null);
                        }}
                        disabled={sendingMagicClubId === club.id}
                        className="btn-secondary text-sm py-2 px-3 flex items-center gap-2 disabled:opacity-60"
                        data-testid={`send-magic-link-${club.slug}`}
                      >
                        <Envelope size={16} /> {sendingMagicClubId === club.id ? 'Sending…' : 'Email admin link'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {/* Tournament Progress Tab */}
        {activeTab === 'tournament' && (
          <TournamentProgressPanel getAuthHeader={getAuthHeader} />
        )}

        {/* Open Golf Settings Tab */}
        {activeTab === 'golf' && (
          <OpenGolfSettingsPanel getAuthHeader={getAuthHeader} />
        )}

        {/* Onboardings Tab */}
        {activeTab === 'onboardings' && (
          <div>
            {importResult && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg" data-testid="import-success">
                <p className="font-bold mb-1">Club Imported Successfully!</p>
                <p className="text-sm">Club URL: <span className="font-mono">{importResult.club_url || `/worldcup2026/${importResult.slug}`}</span></p>
                <p className="text-sm">Admin Email: <span className="font-mono">{importResult.admin_email}</span></p>
                <p className="text-sm">Admin Password: <span className="font-mono font-bold">{importResult.admin_password}</span></p>
                <p className="text-xs text-green-600 mt-2">Save these credentials — the password won't be shown again.</p>
                <button onClick={() => setImportResult(null)} className="mt-2 text-xs underline text-green-700">Dismiss</button>
              </div>
            )}

            {onboardLoading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
              </div>
            ) : onboardings.length === 0 ? (
              <div className="card p-12 text-center">
                <p className="text-gray-400 mb-2">No onboardings yet.</p>
                <p className="text-gray-500 text-sm">When clubs sign up via <span className="font-mono">/onboard</span>, they'll appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {onboardings.map((ob) => (
                  <div key={ob.id} className="card p-4" data-testid={`onboard-row-${ob.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{ob.club_name}</h3>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ob.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {ob.payment_status === 'paid' ? 'Paid' : 'Pending'}
                          </span>
                          {ob.imported && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Imported</span>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-gray-500">
                          <p><span className="text-gray-400">Contact:</span> {ob.contact_name} {ob.role && <span className="text-gray-300">({ob.role})</span>}</p>
                          <p><span className="text-gray-400">Email:</span> {ob.email}</p>
                          <p><span className="text-gray-400">Phone:</span> {ob.phone}</p>
                          <p><span className="text-gray-400">Fee:</span> {ob.currency} {ob.entry_fee}</p>
                          <p>
                            <span className="text-gray-400">Colours:</span>{' '}
                            <span className="inline-block w-4 h-4 rounded align-middle border border-gray-200" style={{ backgroundColor: ob.primary_colour }} />{' '}
                            <span className="inline-block w-4 h-4 rounded align-middle border border-gray-200" style={{ backgroundColor: ob.secondary_colour }} />
                          </p>
                          <p><span className="text-gray-400">Date:</span> {new Date(ob.created_at).toLocaleDateString()}</p>
                        </div>
                        {/* Second Officer */}
                        {ob.officer2_name && (
                          <div className="mt-2 pt-2 border-t text-sm text-gray-500" style={{ borderColor: 'var(--border-color)' }}>
                            <p className="text-xs font-bold text-gray-400 mb-1">2nd Officer</p>
                            <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1">
                              <p>{ob.officer2_name} {ob.officer2_role && <span className="text-gray-300">({ob.officer2_role})</span>}</p>
                              <p>{ob.officer2_email}</p>
                              <p>{ob.officer2_phone}</p>
                            </div>
                          </div>
                        )}
                        {/* Bank Statement */}
                        {ob.bank_statement_filename && (
                          <div className="mt-2">
                            <a
                              href={`${API}/admin/onboardings/${ob.id}/bank-statement`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                              data-testid={`bank-statement-${ob.id}`}
                            >
                              View Bank Statement ({ob.bank_statement_filename})
                            </a>
                          </div>
                        )}
                        {ob.prize_details && (
                          <p className="text-sm text-gray-500 mt-1"><span className="text-gray-400">Prizes:</span> {ob.prize_details}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        {ob.payment_status === 'paid' && !ob.imported && (
                          <button
                            onClick={() => handleImport(ob.id)}
                            disabled={importingId === ob.id}
                            className="btn-primary text-sm py-2 px-4 flex items-center gap-2 whitespace-nowrap"
                            data-testid={`import-btn-${ob.id}`}
                          >
                            {importingId === ob.id ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Importing...
                              </>
                            ) : (
                              <>Import as Club</>
                            )}
                          </button>
                        )}
                        {(ob.payment_status !== 'paid' || ob.imported) && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete onboarding for "${ob.club_name}"?`)) return;
                              try {
                                await axios.delete(`${API}/admin/onboardings/${ob.id}`, getAuthHeader());
                                fetchOnboardings();
                              } catch (err) {
                                alert('Failed: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="btn-secondary text-sm py-2 px-4 flex items-center gap-2 whitespace-nowrap text-red-400 border-red-400/50 hover:border-red-400"
                            data-testid={`delete-onboard-${ob.id}`}
                          >
                            <Trash size={16} /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'demos' && (
          <div data-testid="demos-panel">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Demos & conversions</h2>
                <p className="text-sm text-gray-500 mt-1">A demo converts when a paid club signs up with a matching email OR club name.</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={downloadDemosCsv}
                  className="btn-secondary text-sm py-2 px-3 flex items-center gap-2"
                  data-testid="download-demos-csv"
                >
                  Download CSV
                </button>
                <button
                  onClick={sendWeeklyDigestNow}
                  disabled={digestSending}
                  className="btn-secondary text-sm py-2 px-3 flex items-center gap-2 disabled:opacity-60"
                  data-testid="send-digest-now"
                >
                  <Envelope size={14} /> {digestSending ? 'Sending…' : 'Email me a snapshot'}
                </button>
              </div>
            </div>

            {demosLoading && <p className="text-gray-500 text-sm">Loading…</p>}
            {!demosLoading && demos && (
              <>
                {/* Summary stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <StatCard label="Total demos" value={demos.total} accent="#1D3557" />
                  <StatCard label="Converted" value={demos.converted} accent="#16a34a" />
                  <StatCard label="Conversion rate" value={`${demos.conversion_rate}%`} accent="#635BFF" />
                  <StatCard label="Marketing opt-ins" value={demos.marketing_opt_ins} accent="#d97706" />
                </div>

                {/* Demos table */}
                {demos.demos.length === 0 ? (
                  <div className="card p-8 text-center text-gray-500">No demos yet.</div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="demos-table">
                        <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                          <tr>
                            <th className="px-4 py-3 text-left">Club</th>
                            <th className="px-4 py-3 text-left">Contact</th>
                            <th className="px-4 py-3 text-left">Created</th>
                            <th className="px-4 py-3 text-left">Expires</th>
                            <th className="px-4 py-3 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {demos.demos.map((d, i) => (
                            <tr key={i} className="border-t border-gray-100" data-testid={`demo-row-${d.demo_id}`}>
                              <td className="px-4 py-3">
                                <div className="font-semibold text-gray-900">{d.club_name}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                                  <span className="inline-block w-3 h-3 rounded-full border border-gray-200" style={{ background: d.primary_color }}></span>
                                  {d.currency} {d.entry_fee}
                                  <span className="text-gray-400" data-testid={`demo-sport-${d.demo_id}`}>· {sportMeta(d.predictor_type).label}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-gray-900">{d.email}</div>
                                <div className="text-xs text-gray-500">{d.role}{d.marketing_opt_in && <span className="ml-2 text-amber-600" title="Opted in to marketing">📬</span>}</div>
                              </td>
                              <td className="px-4 py-3 text-gray-600 text-xs">{d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : '—'}</td>
                              <td className="px-4 py-3 text-gray-600 text-xs">{d.expires_at ? new Date(d.expires_at).toLocaleDateString('en-GB') : '—'}</td>
                              <td className="px-4 py-3">
                                {d.converted_is_live ? (
                                  <a href={`/${sportMeta(d.converted_predictor_type || d.predictor_type).base}/${d.converted_to_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-bold rounded">LIVE</a>
                                ) : d.converted ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded">Paid</span>
                                ) : d.reminder_sent_at ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded">Reminded</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">Demo</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-2xl font-black" style={{ color: accent }}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  );
}
